import { useCallback } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Box, Card, CardActionArea, CardContent, Typography } from "@mui/material";
import { formatPercentage } from "score-common";
import PageShell from "../components/PageShell";
import { fetchLivePhone, fetchPublicPhoneChanges } from "../api";
import { useBoardChangeCheck } from "../hooks/useBoardChangeCheck";
import { isScorePubliclyVisible } from "common-fe/scoreboard/publicScoreVisibility.js";

// Card #3307 -- the spectators' Live page, `/live/:code`. One short link per
// show (the code is minted by score-api's ensureShowLiveCode and printed as a
// QR on the board's Blank/Image screens), opening a phone-first list: per
// ring, who is riding now, the last score, who is on deck. A tap on a ride
// opens that class's full results page (`/results/:showId/:year/:classNumber`,
// the page the board's QR already links to).
//
// Data: ONLY the CloudFront-cached phone prefix (api.js fetchLivePhone ->
// /public/scoreboard/phone/live/:code), on the server-driven cadence the
// reply carries (`pollAfterMs`, plus jitter so a crowd that loaded together
// does not poll in lockstep). It never reads the uncached arena route.
// Card #3560: each beat is a CHANGE CHECK against the cached phone change signal
// (useBoardChangeCheck), and the page is pulled only when the show moved (or the
// 60 s backstop ran out), so an idle show costs a crowd one tiny cached read per
// beat instead of the whole page.
//
// Like the board, it never shows an error: a failed or missing first fetch
// reads as an empty page and keeps polling, and a failed later poll keeps the
// last good data on screen (the shared poll never clears `data`). The edge
// caches a 404 for 10s (#3302), so a code that is not live yet resolves by
// itself.
function scoreText(entry) {
  if (entry.termReason) return entry.termReason;
  if (isScorePubliclyVisible(entry.score)) return formatPercentage(entry.score);
  return null;
}

function EntryRow({ label, entry, showId, year, testId }) {
  if (!entry) {
    return (
      <Box data-testid={`${testId}-empty`} sx={{ py: 1 }}>
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          &mdash;
        </Typography>
      </Box>
    );
  }
  const score = scoreText(entry);
  const body = (
    <Box sx={{ py: 1, minHeight: 44 }}>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
        {entry.competitorNumber != null ? `#${entry.competitorNumber} ` : ""}
        {entry.riderName}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {[entry.horseName, entry.className].filter(Boolean).join(" · ")}
      </Typography>
      {score ? (
        <Typography variant="h6" sx={{ fontWeight: 700 }} data-testid={`${testId}-score`}>
          {score}
          {entry.rank != null && entry.ofCount != null && !entry.termReason ? (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {`${entry.rank} of ${entry.ofCount}`}
            </Typography>
          ) : null}
        </Typography>
      ) : null}
    </Box>
  );
  if (entry.classNumber == null || !showId) {
    return <Box data-testid={testId}>{body}</Box>;
  }
  return (
    <CardActionArea
      component={RouterLink}
      to={`/results/${encodeURIComponent(showId)}/${encodeURIComponent(year ?? "")}/${encodeURIComponent(entry.classNumber)}`}
      data-testid={testId}
      sx={{ display: "block" }}
    >
      {body}
    </CardActionArea>
  );
}

export default function LivePage() {
  const { code } = useParams();
  const fetchPayload = useCallback(() => fetchLivePhone({ code }), [code]);
  const { data } = useBoardChangeCheck(fetchPayload, fetchPublicPhoneChanges, { phone: true });

  const rings = Array.isArray(data?.rings) ? data.rings : [];

  return (
    <PageShell>
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }} data-testid="live-show-name">
        {data?.showName || "Live"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Live results. This page updates by itself.
      </Typography>
      {rings.length === 0 ? (
        <Typography variant="body1" color="text.secondary" data-testid="live-empty">
          Nothing is running yet. This page will fill in as rides start.
        </Typography>
      ) : (
        rings.map((ring) => (
          <Card key={ring.ring} variant="outlined" sx={{ mb: 2 }} data-testid="live-ring">
            <CardContent sx={{ pb: 1 }}>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }} data-testid="live-ring-label">
                {ring.label}
              </Typography>
              <EntryRow label="Now riding" entry={ring.current} showId={data.showId} year={data.year} testId="live-current" />
              <EntryRow label="Last score" entry={ring.lastCompleted} showId={data.showId} year={data.year} testId="live-last" />
              <EntryRow label="On deck" entry={ring.onDeck} showId={data.showId} year={data.year} testId="live-ondeck" />
            </CardContent>
          </Card>
        ))
      )}
    </PageShell>
  );
}
